import React, { useState, useEffect } from 'react';
import useCustomerOptions from '../../hooks/useCustomerOptions';
import { useDeviceBrands } from '../../modules/devices';
import { medicalDeviceData } from '../../data/medicalDeviceData';
import { syncBrandAndModel } from '../../utils/brandSync'; // YENİ
import { findDuplicateDeviceBySerial, duplicateDeviceMessage } from '../../utils/deviceDedup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Box, Autocomplete, createFilterOptions, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const filter = createFilterOptions();

const DeviceFormModal = ({ isOpen, onClose, onAddDevice, onUpdateDevice, deviceToEdit }) => {
    // Müşteri listesi: paylaşımlı oturum cache'i (modal başına tam koleksiyon okuması yok).
    const { customers } = useCustomerOptions();
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [seriNumarasi, setSeriNumarasi] = useState('');
    const [marka, setMarka] = useState('');
    const [markaInputValue, setMarkaInputValue] = useState('');
    const [model, setModel] = useState('');
    const [modelInputValue, setModelInputValue] = useState('');
    const [faturaNo, setFaturaNo] = useState('');

    const [verilisTarihi, setVerilisTarihi] = useState(null);
    const [garantiBaslangic, setGarantiBaslangic] = useState(null);
    const [garantiBitis, setGarantiBitis] = useState(null);
    const [periyodikBakimTarihi, setPeriyodikBakimTarihi] = useState(null);

    // Yeni Alanlar
    const [cihazSinifi, setCihazSinifi] = useState('Oksijen Konsantratörü');
    const [isSgk, setIsSgk] = useState(false);
    const [isHurda, setIsHurda] = useState(false); // YENİ
    const [zimmetTarihi, setZimmetTarihi] = useState(null);

    const [isSaving, setIsSaving] = useState(false);

    // Marka/Model listesi: ortak useDeviceBrands hook'u (paylaşımlı oturum cache'i).
    // Burada ayrıca ikinci bir "brands" listener'ı açılıyordu — aynı veriyi iki kez
    // okuyan mükerrer aboneydi, kaldırıldı.
    const { brandsFromDb } = useDeviceBrands();
    // Custom brands state (user entering new brand manually)
    const [customBrands, setCustomBrands] = useState([]);

    // Autocomplete için tüm markalar (DB'den + static'den + custom)
    const allBrands = [...new Set([
        ...brandsFromDb.map(b => b.name),
        ...Object.keys(medicalDeviceData),
        ...customBrands
    ])].sort();

    // Model listesi - seçilen markaya göre
    const getModelsForBrand = (brandName) => {
        const dbBrand = brandsFromDb.find(b => b.name === brandName);
        const dbModels = dbBrand?.models?.map(m => m.model) || [];
        const staticModels = medicalDeviceData[brandName]?.map(m => m.model) || [];

        return [...new Set([...dbModels, ...staticModels])].sort();
    };

    const toDateOrNull = (timestamp) => {
        if (!timestamp) return null;
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            return timestamp.toDate();
        }
        // Eğer zaten Date objesiyse
        if (timestamp instanceof Date) return timestamp;
        return null;
    };

    useEffect(() => {
        if (isOpen && deviceToEdit) {
            setSeriNumarasi(deviceToEdit.seriNumarasi || '');
            setMarka(deviceToEdit.marka || '');
            setModel(deviceToEdit.model || '');
            setFaturaNo(deviceToEdit.faturaNo || '');

            // Tarih alanları
            setVerilisTarihi(toDateOrNull(deviceToEdit.verilisTarihi));
            setGarantiBaslangic(toDateOrNull(deviceToEdit.garantiBaslangic));
            setGarantiBitis(toDateOrNull(deviceToEdit.garantiBitis));
            setPeriyodikBakimTarihi(toDateOrNull(deviceToEdit.periyodikBakimTarihi));

            // Diğer detaylar
            setCihazSinifi(deviceToEdit.cihazSinifi || 'Oksijen Konsantratörü');
            setIsSgk(deviceToEdit.isSgk || false);
            setIsHurda(deviceToEdit.isHurda || false);
            setZimmetTarihi(toDateOrNull(deviceToEdit.zimmetTarihi));

            // Müşteri eşleştirme
            if (customers.length > 0 && deviceToEdit.musteriId) {
                const foundCustomer = customers.find(c => c.id === deviceToEdit.musteriId);
                if (foundCustomer) {
                    setSelectedCustomer(foundCustomer);
                }
            }
        } else if (isOpen) {
            resetForm();
        }
    }, [deviceToEdit, isOpen, customers]);

    const resetForm = () => {
        setSeriNumarasi('');
        setMarka('');
        setMarkaInputValue('');
        setModel('');
        setModelInputValue('');
        setFaturaNo('');
        setSelectedCustomer(null);
        setVerilisTarihi(null);
        setGarantiBaslangic(null);
        setGarantiBitis(null);
        setPeriyodikBakimTarihi(null);
        setCihazSinifi('Oksijen Konsantratörü');
        setIsSgk(false);
        setIsHurda(false);
        setZimmetTarihi(null);
        setIsSaving(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleBrandChange = (event, newValue) => {
        if (typeof newValue === 'string') {
            setMarka(newValue);
        } else if (newValue && newValue.inputValue) {
            // Yeni marka eklendi
            setMarka(newValue.inputValue);
            setCustomBrands(prev => [...prev, newValue.inputValue]);
        } else {
            setMarka(newValue || '');
        }
        setModel('');
        setModelInputValue('');
    };

    const handleModelChange = (event, newValue) => {
        if (typeof newValue === 'string') {
            setModel(newValue);
        } else if (newValue && newValue.inputValue) {
            setModel(newValue.inputValue);
        } else {
            setModel(newValue || '');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        const finalMarka = marka || markaInputValue;
        const finalModel = model || modelInputValue;

        if (!selectedCustomer || !seriNumarasi || !finalMarka) {
            alert('Müşteri, Seri Numarası ve Marka alanları zorunludur.');
            setIsSaving(false);
            return;
        }

        // Seri Numarası Mükerrer Kontrolü (ortak util — normalize edilmiş).
        // Düzenleme modunda kendi kaydı hariç tutulur (excludeId); böylece seri no
        // değişmediyse veya BAŞKA bir cihazla çakışmıyorsa kayda izin verilir.
        try {
            const duplicate = await findDuplicateDeviceBySerial(seriNumarasi, { excludeId: deviceToEdit?.id });
            if (duplicate) {
                alert(duplicateDeviceMessage(duplicate, seriNumarasi));
                setIsSaving(false);
                return;
            }
        } catch (error) {
            console.error("Seri numarası duplicate kontrol hatası:", error);
        }

        const finalDeviceData = {
            musteriId: selectedCustomer.id,
            musteriAdSoyad: selectedCustomer.adSoyad,
            seriNumarasi: seriNumarasi.trim(),
            marka: finalMarka,
            model: finalModel,
            faturaNo,
            verilisTarihi,
            garantiBaslangic,
            garantiBitis,
            periyodikBakimTarihi,
            cihazSinifi,
            isSgk,
            isHurda, // YENİ
            zimmetTarihi: isSgk ? zimmetTarihi : null
        };


        if (deviceToEdit) {
            await onUpdateDevice(deviceToEdit.id, finalDeviceData);
        } else {
            await onAddDevice(finalDeviceData);
        }

        // YENİ: Marka/Model senkronizasyonu
        await syncBrandAndModel(finalMarka, finalModel);

        handleClose();
    };

    if (!isOpen) return null;
    const isEditing = !!deviceToEdit;
    const modelsForSelectedBrand = getModelsForBrand(marka);

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditing ? 'Cihaz Bilgilerini Düzenle' : 'Yeni Cihaz Ekle'}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Autocomplete
                        options={customers}
                        getOptionLabel={(option) => option.adSoyad || ''}
                        value={selectedCustomer}
                        onChange={(event, newValue) => setSelectedCustomer(newValue)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                                <li key={option.id} {...otherProps}>
                                    {option.adSoyad}
                                </li>
                            );
                        }}
                        renderInput={(params) => <TextField {...params} label="Müşteri" required />}
                    />

                    <TextField label="Seri Numarası" value={seriNumarasi} onChange={(e) => setSeriNumarasi(e.target.value)} fullWidth required />
                    <TextField label="Fatura No (Opsiyonel)" value={faturaNo} onChange={(e) => setFaturaNo(e.target.value)} fullWidth />

                    <Autocomplete
                        freeSolo
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        options={allBrands}
                        value={marka}
                        onChange={handleBrandChange}
                        inputValue={markaInputValue}
                        onInputChange={(event, newInputValue) => setMarkaInputValue(newInputValue)}
                        filterOptions={(options, params) => {
                            const filtered = filter(options, params);
                            const { inputValue } = params;
                            const isExisting = options.some((option) => inputValue === option);
                            if (inputValue !== '' && !isExisting) {
                                filtered.push({
                                    inputValue,
                                    label: `"${inputValue}" ekle`
                                });
                            }
                            return filtered;
                        }}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if (option.inputValue) return option.inputValue;
                            return option.label || '';
                        }}
                        renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                                <li key={key} {...otherProps}>
                                    {typeof option === 'string' ? option : option.label}
                                </li>
                            );
                        }}
                        renderInput={(params) => <TextField {...params} label="Marka" required />}
                    />

                    <Autocomplete
                        freeSolo
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        options={modelsForSelectedBrand}
                        value={model}
                        onChange={handleModelChange}
                        inputValue={modelInputValue}
                        onInputChange={(event, newInputValue) => setModelInputValue(newInputValue)}
                        filterOptions={(options, params) => {
                            const filtered = filter(options, params);
                            const { inputValue } = params;
                            const isExisting = options.some((option) => inputValue === option);
                            if (inputValue !== '' && !isExisting) {
                                filtered.push({
                                    inputValue,
                                    label: `"${inputValue}" ekle`
                                });
                            }
                            return filtered;
                        }}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if (option.inputValue) return option.inputValue;
                            return option.label || '';
                        }}
                        renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                                <li key={key} {...otherProps}>
                                    {typeof option === 'string' ? option : option.label}
                                </li>
                            );
                        }}
                        renderInput={(params) => <TextField {...params} label="Model" />}
                        disabled={!marka}
                    />

                    <FormControl fullWidth>
                        <InputLabel>Cihaz Sınıfı</InputLabel>
                        <Select
                            value={cihazSinifi}
                            label="Cihaz Sınıfı"
                            onChange={(e) => setCihazSinifi(e.target.value)}
                        >
                            <MenuItem value="Oksijen Konsantratörü">Oksijen Konsantratörü</MenuItem>
                            <MenuItem value="Ventilatör">Ventilatör</MenuItem>
                            <MenuItem value="Cpap">Cpap</MenuItem>
                            <MenuItem value="Auto Cpap">Auto Cpap</MenuItem>
                            <MenuItem value="Bpap">Bpap</MenuItem>
                            <MenuItem value="Bpap ST">Bpap ST</MenuItem>
                            <MenuItem value="Avaps">Avaps</MenuItem>
                            <MenuItem value="ASV">ASV</MenuItem>
                            <MenuItem value="Diğer">Diğer</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ p: 2, border: '1px solid #ff9800', borderRadius: 1, backgroundColor: '#fff3e0' }}>
                        <Typography variant="subtitle2" color="warning.dark" gutterBottom>SGK Durumu</Typography>
                        <FormControlLabel
                            control={<Checkbox checked={isSgk} onChange={(e) => setIsSgk(e.target.checked)} color="warning" />}
                            label="Bu cihaz SGK zimmetli mi?"
                        />
                        {isSgk && (
                            <DatePicker
                                label="Zimmet Tarihi"
                                value={zimmetTarihi}
                                onChange={setZimmetTarihi}
                                sx={{ mt: 2, width: '100%' }}
                            />
                        )}
                    </Box>

                    {/* Hurda Durumu */}
                    <Box sx={{ p: 2, border: '1px solid #d32f2f', borderRadius: 1, backgroundColor: '#ffebee' }}>
                        <Typography variant="subtitle2" color="error.dark" gutterBottom>Hurda Durumu</Typography>
                        <FormControlLabel
                            control={<Checkbox checked={isHurda} onChange={(e) => setIsHurda(e.target.checked)} color="error" />}
                            label="Bu cihaz HURDA olarak işaretlensin mi?"
                        />
                    </Box>

                    <DatePicker label="Veriliş Tarihi" value={verilisTarihi} onChange={setVerilisTarihi} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <DatePicker label="Garanti Başlangıç" value={garantiBaslangic} onChange={setGarantiBaslangic} sx={{ width: '100%' }} />
                        <DatePicker label="Garanti Bitiş" value={garantiBitis} onChange={setGarantiBitis} sx={{ width: '100%' }} />
                    </Box>
                    <DatePicker label="Sonraki Periyodik Bakım" value={periyodikBakimTarihi} onChange={setPeriyodikBakimTarihi} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={handleClose} disabled={isSaving}>İptal</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
                    {isSaving ? "Kaydediliyor..." : (isEditing ? 'Cihazı Güncelle' : 'Cihazı Kaydet')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeviceFormModal;